import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MainLayout from '../components/layout/MainLayout';
import {
  Modal,
  Card,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  Fade,
  Paper,
  Stack,
  Container,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// APIs
import { fetchCategoryAsync, deleteCategoryAsync, createCategoryAsync, updateCategoryAsync, setError } from '../redux/reducer/category/categorySlice';

const Categories = () => {
  const { appId } = useParams();
  const dispatch = useDispatch();

  const [category, setCategory] = useState('');
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const categories = useSelector((state) => state.category.list);
  const loading = useSelector((state) => state.category.loading);
  const ErrorState = useSelector((state) => state.category.error);

  const [successAlert, setSuccessAlert] = useState(false);

  useEffect(() => {
    dispatch(fetchCategoryAsync(appId));
  }, [dispatch, appId]);

  const handleOpen = () => {
    setOpen(true);
    setSaved(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSaved(false);
    setSaving(false);
    setCategory('');
  };

  const openEditModal = (category) => {
    setIsEditModalOpen(true);
    setSelectedCategory(category);
  };

  const closeEditModal = () => {
    setSelectedCategory(null);
    setIsEditModalOpen(false);
  };

  const handleOpenDeleteModal = (categoryId) => {
    const categoryToDelete = categories.find(cat => cat._id === categoryId);
    setSelectedCategory(categoryToDelete);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleAdd = () => {
    setSaving(true);
    if (!category) {
      dispatch(setError('Category name is required'));
      setSaving(false);
      return;
    }
    try {
      dispatch(createCategoryAsync( appId, { category } ))
        .then(() => {
          setSaving(false);
          dispatch(fetchCategoryAsync(appId));
          setSuccessAlert(true);
          setCategory('');
          handleClose();
        })
        .catch((error) => {
          setSaving(false);
          setSaved(false);
          dispatch(setError(error.message));
          console.error(error);
        });
    } catch (error) {
      setSaving(false);
      setSaved(false);
      dispatch(setError(error.message));
      console.log(error);
    }
  };

  const handleDeleteCategory = () => {
    if (selectedCategory) {
      dispatch(deleteCategoryAsync({ appId, categoryId: selectedCategory._id }))
        .then(() => {
          console.log(`deleted ${selectedCategory._id}`);
          dispatch(fetchCategoryAsync(appId));
          handleCloseDeleteModal();
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const handleUpdateCategory = async (event) => {
    event.preventDefault();

    if (selectedCategory) {
      if (!selectedCategory.category) {
        dispatch(setError('Category name is required'));
        return;
      }

      dispatch(updateCategoryAsync({ appId, categoryId: selectedCategory._id, categoryData: selectedCategory }))
        .then((updatedCategory) => {
          if (updatedCategory) {
            setSaved(true);
            dispatch(fetchCategoryAsync(appId));
            setTimeout(() => {
              closeEditModal();
              setSaved(false);
            }, 1000);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  if (!categories) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress /> <Typography ml={2}>Loading...</Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography
                variant="h4"
                fontWeight="700"
                gutterBottom
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Course Categories
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage course categories for better organization
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpen}
              sx={{
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              Add Category
            </Button>
          </Stack>
        </Box>
      
        {/* Success Alert */}
        {successAlert && (
          <Fade in={true}>
            <Alert 
              severity="success" 
              onClose={() => setSuccessAlert(false)}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              Operation completed successfully!
            </Alert>
          </Fade>
        )}

        {/* Error Alert */}
        {ErrorState && (
          <Fade in={true}>
            <Alert 
              severity="error" 
              onClose={() => dispatch(setError(null))}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {ErrorState}
            </Alert>
          </Fade>
        )}

        {/* Add Category Modal */}
        <Modal
          open={open}
          onClose={handleClose}
          sx={{
            backdropFilter: 'blur(4px)',
          }}
        >
          <Fade in={open}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 500 },
                borderRadius: 3,
                boxShadow: 24,
                maxHeight: '90vh',
                overflow: 'auto',
              }}
            >
              {/* Modal Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  p: 3,
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" fontWeight="600">
                  Create New Category
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Modal Body */}
              <Box sx={{ p: 3 }}>
                {ErrorState && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {ErrorState}
                  </Alert>
                )}
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Category Name"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    required
                  />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleAdd}
                      disabled={saving}
                      sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        },
                      }}
                    >
                      {saving ? (
                        <>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                          Saving...
                        </>
                      ) : saved ? (
                        'Saved!'
                      ) : (
                        'Create Category'
                      )}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Fade>
        </Modal>

        {/* Categories Table */}
        <Card sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S/N</TableCell>
                  <TableCell>Category Name</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : categories && categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <Typography variant="subtitle1" color="text.secondary">
                        No categories available
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Click "Add Category" to create your first category
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((cat, index) => (
                    <TableRow
                      key={cat._id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(99, 102, 241, 0.04)',
                        },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {cat.category}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => openEditModal(cat)}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteModal(cat._id)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              },
                            }}
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Edit Category Modal */}
        {selectedCategory && (
          <Modal
            open={isEditModalOpen}
            onClose={closeEditModal}
            sx={{ backdropFilter: 'blur(4px)' }}
          >
            <Fade in={isEditModalOpen}>
              <Paper
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '90%', sm: 500 },
                  borderRadius: 3,
                  boxShadow: 24,
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    p: 3,
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6" fontWeight="600">
                    Edit Category
                  </Typography>
                  <IconButton onClick={closeEditModal} sx={{ color: 'white' }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Category Name"
                      value={selectedCategory.category}
                      onChange={(e) => setSelectedCategory({ ...selectedCategory, category: e.target.value })}
                      required
                    />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={closeEditModal}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleUpdateCategory}
                        sx={{
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                          },
                        }}
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            Saving...
                          </>
                        ) : saved ? (
                          'Saved!'
                        ) : (
                          'Update Category'
                        )}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Fade>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          sx={{ backdropFilter: 'blur(4px)' }}
        >
          <Fade in={openDeleteModal}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 400 },
                borderRadius: 3,
                boxShadow: 24,
                textAlign: 'center',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 32, color: 'error.main' }} />
              </Box>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Delete Category?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Are you sure you want to delete this category? This action cannot be undone.
              </Typography>
              {selectedCategory && (
                <Box
                  sx={{
                    backgroundColor: 'background.default',
                    p: 2,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" fontWeight="600">
                    {selectedCategory.category}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="outlined" onClick={handleCloseDeleteModal}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteCategory}
                  startIcon={<DeleteForeverIcon />}
                >
                  Delete Category
                </Button>
              </Stack>
            </Paper>
          </Fade>
        </Modal>
      </Container>
    </MainLayout>
  );
};

export default Categories;
